/**
 * Report List Configuration
 * Columns, sort, filter, action definitions
 */
import type { TFunction } from 'i18next'
import type {
    DataTableColumn,
    DataTableAction,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { createChipFormatter } from '@/components/ui/data-table-customized'
import { Eye, Pencil, Trash2, Send, Power, History } from 'lucide-react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import type { ReportWAF } from '../types/response'
import {
    sendFrequencyOptions,
    sendFormatOptions,
    statusOptions,
} from '../types/utils'

export const REPORT_SORT_CONFIG = {
    defaultSort: {
        sort_by: 'created_at',
        sort_order: 'desc' as const,
    },
}

export const REPORT_FILTER_DEFAULTS = {
    send_frequency: '',
    send_format: '',
    status: '',
    priority_min: '',
    priority_max: '',
    registration_date: [] as string[],
}

export const getReportFilterItems = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'status',
        label: t('fields.status'),
        type: 'select',
        options: statusOptions(t),
    },
    {
        name: 'send_frequency',
        label: t('fields.send_frequency'),
        type: 'select',
        options: sendFrequencyOptions(t),
    },
    {
        name: 'send_format',
        label: t('fields.send_format'),
        type: 'select',
        options: sendFormatOptions(t),
    },
    {
        name: 'priority_min',
        label: t('priority_level_min'),
        type: 'custom',
        component: null,
    },
    {
        name: 'priority_max',
        label: t('priority_level_max'),
        type: 'custom',
        component: null,
    },
    {
        name: 'registration_date',
        label: t('registration_date'),
        type: 'daterangepicker',
        names: ['registration_date_from', 'registration_date_to'],
    },
]

export const getReportColumns = (
    t: TFunction
): DataTableColumn<ReportWAF>[] => [
    {
        key: 'customer_name',
        title: t('customer_name'),
        width: 300,
        pinned: 'left',
        cellClassName: 'truncate',
        sortable: true,
        render: (_, row) => (
            <Link
                to={`/technical/report/${row.id}/history`}
                className="text-primary hover:underline uppercase"
            >
                {row.customer_id} - {row.customer_name}
            </Link>
        ),
    },
    {
        key: 'service_type',
        title: t('fields.service_type', { defaultValue: 'Service' }),
        width: 80,
    },
    {
        key: 'send_frequency',
        title: t('fields.send_frequency'),
        width: 90,
        render: (value) =>
            t(`send_frequency.${value}`, { defaultValue: value as string }),
    },
    {
        key: 'send_format',
        title: t('fields.send_format'),
        width: 100,
        render: (value) =>
            t(`send_format.${value}`, { defaultValue: value as string }),
    },
    {
        key: 'status',
        title: t('fields.status'),
        width: 90,
        formatter: createChipFormatter({
            colorMap: {
                activate: { color: 'success' },
                deactivate: { color: 'default' },
            },
        }),
    },
    {
        key: 'last_send_date',
        title: t('last_send_date'),
        width: 160,
        render: (value) =>
            value ? dayjs(value as string).format('DD/MM/YYYY HH:mm') : '-',
    },
]

export const getReportActions = (
    t: TFunction,
    handlers: {
        onResend: (row: ReportWAF) => void
        onToggleStatus: (row: ReportWAF) => void
        onViewHistory: (row: ReportWAF) => void
        onView: (row: ReportWAF) => void
        onEdit: (row: ReportWAF) => void
        onDelete: (row: ReportWAF) => void
    }
): DataTableAction<ReportWAF>[] => [
    {
        key: 'resend',
        label: t('actions.resend'),
        icon: <Send className="w-4 h-4" />,
        onClick: handlers.onResend,
    },
    {
        key: 'toggle_status',
        label: t('actions.toggle_status'),
        icon: <Power className="w-4 h-4" />,
        onClick: handlers.onToggleStatus,
    },
    {
        key: 'view_history',
        label: t('actions.view_history'),
        icon: <History className="w-4 h-4" />,
        onClick: handlers.onViewHistory,
    },
    {
        key: 'view',
        label: t('actions.view'),
        icon: <Eye className="w-4 h-4" />,
        onClick: handlers.onView,
    },
    {
        key: 'edit',
        label: t('edit'),
        icon: <Pencil className="w-4 h-4" />,
        onClick: handlers.onEdit,
    },
    {
        key: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 className="w-4 h-4" />,
        variant: 'danger',
        onClick: handlers.onDelete,
    },
]
